/**
 * Re-key every Generate template with the print guide key (owner, 2026-10-14: "re-key all").
 * Each template's original print guide image is keyed again, and its case layer, print mask and
 * matte are replaced; everything else in its bake stays. A published version is locked, so the
 * new layers go on a new version, which is then published the way Templates ▸ Activate does
 * (owner, 2026-10-14: "I approve draft also").
 */
import type { WireView } from "@genz/engine-bind";
import type { JsonClient } from "@genz/platform";
import { changesGuide, finishedPixels } from "../editor/cameraShading.js";
import { chromaKeyFromPristine, type GuideKeyOptions } from "../editor/chromaKey.js";
import { loadImagePixels, rgbaToPngBytes } from "../editor/pixels.js";
import { mergeViewRefs, refsForView, type BakedAssetRefs, type BakedAssetRefsMap } from "./bakedSync.js";
import { viewTypes } from "./forceSync.js";
import { PRIMARY_VIEW } from "./views.js";
import { hostBakedPng } from "./bakedSyncCloud.js";

export type RekeyOutcome = "rekeyed" | "republished" | "skipped";

type RekeyClient = Pick<JsonClient,
  "getTemplate" | "createVersion" | "setBakedAssets" | "templateReferenceUpload" | "submitVersion" | "approveVersion" | "approvalPolicy">;

function grayPng(plane: Uint8Array, width: number, height: number): Uint8Array {
  const rgba = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < plane.length; i++) rgba.set([plane[i]!, plane[i]!, plane[i]!, 255], i * 4);
  const png = rgbaToPngBytes(rgba, width, height);
  if (!png) throw new Error("the mask could not be encoded");
  return png;
}

/** The view's print guide as the editor keys it: flattened and shading-fixed as the view says. */
function viewPixels(view: WireView, imageUrl: string) {
  return changesGuide(view) ? finishedPixels(imageUrl, view) : loadImagePixels(imageUrl);
}

/** The new case layer, print mask and matte of one view, keyed from its original image. */
async function rekeyView(client: RekeyClient, templateId: string, view: WireView, imageUrl: string, key: GuideKeyOptions) {
  const { data, width, height } = await viewPixels(view, imageUrl);
  const keyed = chromaKeyFromPristine(data, width, height, true, key);
  if (!keyed) return null;
  const n = width * height;
  const shell = new Uint8ClampedArray(keyed.out);
  const printMask = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const source = data[i * 4 + 3]!;
    printMask[i] = source ? Math.round(255 * (1 - keyed.alpha[i]! / source)) : 0;
    // As the bake's product mask does: a pixel the key left almost clear is not the case.
    if (shell[i * 4 + 3]! <= 8) shell[i * 4 + 3] = 0;
  }
  const shellPng = rgbaToPngBytes(shell, width, height);
  if (!shellPng) throw new Error("the case layer could not be encoded");
  const [shell_url, print_mask_url, matte_url] = await Promise.all([
    hostBakedPng(client, templateId, shellPng),
    hostBakedPng(client, templateId, grayPng(printMask, width, height)),
    hostBakedPng(client, templateId, grayPng(keyed.alpha, width, height)),
  ]);
  return { shell_url, print_mask_url, matte_url };
}

export async function rekeyTemplate(client: RekeyClient, templateId: string, key: GuideKeyOptions): Promise<RekeyOutcome> {
  const template = await client.getTemplate(templateId);
  const version = template.currentVersion;
  const baked = version?.bakedAssets as BakedAssetRefsMap | null | undefined;
  if (!version || !baked?.shaded_magenta_guide) return "skipped";
  const payload = version.payload;
  let next: BakedAssetRefsMap = baked;
  let changed = false;
  for (const view of viewTypes(payload?.views)) {
    const own = refsForView(baked, view);
    const wire = payload?.views?.find((v) => (v.view_type ?? PRIMARY_VIEW) === view);
    const imageUrl = wire?.base_case_image_ref;
    if (!own || !wire || typeof imageUrl !== "string" || !/^https?:/.test(imageUrl)) continue;
    const layers = await rekeyView(client, templateId, wire, imageUrl, key);
    if (!layers) continue;
    const refs: BakedAssetRefs = { ...own, ...layers, matte_kind: "chroma", edge_feather_px: 0, edge_choke_px: 0, matte_strength: 0 };
    next = mergeViewRefs(next, view, refs);
    changed = true;
  }
  if (!changed) return "skipped";
  if (version.status === "Draft") {
    await client.setBakedAssets(templateId, version.versionNumber, next as unknown as Record<string, unknown>);
    return "rekeyed";
  }
  const draft = await client.createVersion(templateId);
  const updated = await client.setBakedAssets(templateId, draft.versionNumber, next as unknown as Record<string, unknown>);
  // As the editor submits: every view whole, so nothing of the saved geometry is lost.
  const views = payload?.views ?? [];
  const primary = views.find((v) => v.view_type === PRIMARY_VIEW) ?? views[0];
  await client.submitVersion(
    templateId, draft.versionNumber, updated.versionToken,
    (primary as unknown as { masks?: unknown })?.masks ?? [],
    primary?.brand_logo_placement ?? null,
    views as unknown as { view_type: string; masks: unknown; layers?: unknown[] }[],
  );
  if ((await client.approvalPolicy()).approvals_enabled) await client.approveVersion(templateId, draft.versionNumber);
  return "republished";
}
